import React, { useEffect, useRef, useState } from 'react';
import { Building2, Globe, ChevronDown, Check } from 'lucide-react';
import { useDashboardTranslation } from '../i18n';

/**
 * Selector de compañía del tablero de indicadores.
 *
 * <b>Es una copia deliberada del de MasterCrud, no una abstracción compartida.</b>
 * Ese es el patrón del kit: `MasterCrud` tiene el suyo, `MasterPatternView` tiene
 * otro (`toolbar/CompanySelector`) y `MasterDocument` monta el propio. Cada módulo
 * necesita props distintas —aquí hace falta buscador y opciones deshabilitadas con
 * motivo, que los maestros no piden— y factorizarlos en uno solo obligaría a tocar
 * un componente del que dependen los maestros para servir a una pantalla nueva.
 *
 * <b>El aspecto es idéntico al de Ítems, a propósito.</b> Mismo chip azul de marca, mismo icono
 * contextual —globo cuando el alcance es Global, edificio cuando es una compañía—, mismo panel
 * con divisor, mismo check azul en la activa. Un usuario que cambia de compañía en Ítems y luego
 * en el tablero tiene que estar haciendo el mismo gesto sobre el mismo control.
 *
 * <b>Los colores salen de la escala `primary-*` del kit, no de hexadecimales.</b> El chip usaba
 * siete literales y seis de ellos ERAN esos tokens (`#93d1fd` = `primary-300`,
 * `#1355b7` = `primary-800`, `#dbeefe` = `primary-100`, `#3c9bf6` = `primary-500`,
 * `#0e79fd` = `primary-600`), mientras su propia variante oscura ya los nombraba. La regla del
 * módulo (`index.css`) es que ningún componente del tablero declare un hexadecimal; este control
 * es del ERP y no una tarjeta del tablero, así que su excepción es usar `primary-*` en vez de
 * `--dash-*` — no cablear el valor.
 *
 * Lo que este añade y los maestros no tienen:
 *
 *  - <b>Buscador</b>, porque un tablero de dirección se consulta en organizaciones
 *    con muchas compañías y un panel sin filtro deja de ser usable.
 *  - <b>Opciones deshabilitadas con su motivo</b>, para `Global`: en un maestro
 *    significa "registro que aplica a todas"; en un tablero significaría consolidar
 *    varias compañías, que es otra operación — y sumar cifras en monedas distintas
 *    da un número creíble y sin significado.
 */

export interface DashboardCompany {
  uuid: string;
  name: string;
  code?: string | null;
  /** Si el usuario la tiene habilitada. `undefined` oculta la pill de estado. */
  enabled?: boolean;
}

/** El uuid con el que los maestros representan el alcance Global. Mismo convenio. */
export const GLOBAL_UUID = '000';

/** A partir de cuántas compañías aparece el buscador solo. */
export const SEARCH_THRESHOLD = 8;

export interface DashboardCompanySelectorProps {
  companies: DashboardCompany[];
  selectedCompanyId?: string | null;
  onCompanyChange: (company: DashboardCompany) => void;
  /**
   * Opciones visibles pero no elegibles, con el motivo como tooltip.
   * `{ '000': 'Las compañías habilitadas manejan monedas distintas' }`
   *
   * <b>Se muestran en vez de ocultarse.</b> Quien conoce una opción de otra pantalla
   * y no la encuentra aquí asume que falta o que algo se rompió; verla apagada con
   * su motivo le dice que existe y por qué hoy no aplica.
   */
  optionDisabledReasons?: Record<string, string>;
  /** `'auto'` enciende el buscador a partir de {@link SEARCH_THRESHOLD} compañías. */
  searchable?: boolean | 'auto';
  disabled?: boolean;
  className?: string;
}

export const DashboardCompanySelector: React.FC<DashboardCompanySelectorProps> = ({
  companies,
  selectedCompanyId,
  onCompanyChange,
  optionDisabledReasons,
  searchable = 'auto',
  disabled = false,
  className = '',
}) => {
  const { t } = useDashboardTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      // Se limpia al cerrar: reabrir con el filtro de la vez anterior puesto
      // parecería una lista incompleta.
      setQuery('');
      return;
    }
    // Autofocus: quien abre el panel para buscar ya sabe qué va a teclear.
    searchRef.current?.focus();

    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!companies.length) return null;

  const selected = companies.find(c => String(c.uuid) === String(selectedCompanyId));
  const isGlobal = !selected || String(selected.uuid) === GLOBAL_UUID;
  const ContextIcon = isGlobal ? Globe : Building2;
  const chipLabel = selected?.name ?? t('dashboard.company.placeholder');

  /** Filtra por nombre Y por código: quien conoce el código lo teclea antes que el nombre. */
  const matches = (c: DashboardCompany) => {
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    return String(c.name ?? '').toLowerCase().includes(needle)
      || String(c.code ?? '').toLowerCase().includes(needle);
  };

  const visible = companies.filter(matches);
  const globals = visible.filter(c => String(c.uuid) === GLOBAL_UUID);
  const reales = visible.filter(c => String(c.uuid) !== GLOBAL_UUID);

  const showSearch = searchable === true
    || (searchable === 'auto' && companies.length >= SEARCH_THRESHOLD);

  // Mismo chip que el maestro de Ítems, literalmente: azul de marca, no tokens del
  // tablero. Es un control del ERP, no una tarjeta del tablero, y tiene que leerse
  // igual en las dos pantallas.
  const chipClasses = [
    'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold leading-none select-none transition-colors',
    'bg-primary-50 dark:bg-primary-900/20 border border-primary-300 dark:border-primary-800 text-primary-800 dark:text-primary-300',
    disabled
      ? 'opacity-60 cursor-not-allowed'
      : 'cursor-pointer hover:bg-primary-100 dark:hover:bg-primary-900/40 hover:border-primary-500',
  ].join(' ');

  const renderOption = (company: DashboardCompany) => {
    const optIsGlobal = String(company.uuid) === GLOBAL_UUID;
    const OptIcon = optIsGlobal ? Globe : Building2;
    const active = String(company.uuid) === String(selectedCompanyId);
    const reason = optionDisabledReasons?.[String(company.uuid)];

    return (
      <button
        key={company.uuid}
        type="button"
        role="menuitem"
        disabled={!!reason}
        title={reason}
        onClick={() => { if (reason) return; onCompanyChange(company); setOpen(false); }}
        className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors ${
          reason ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[var(--dash-surface-page)]'
        }`}
        style={{ color: 'var(--dash-text-primary)' }}
        data-testid={`company-option-${company.uuid}`}
      >
        <span className="w-3 flex-shrink-0 text-primary-600">
          {active && <Check className="w-3 h-3" />}
        </span>
        <span className="flex-shrink-0 text-primary-800 dark:text-primary-300">
          <OptIcon className="w-3.5 h-3.5" />
        </span>
        <span className="font-medium truncate min-w-0" title={company.name}>{company.name}</span>

        <span className="ml-auto flex items-center gap-2 flex-shrink-0">
          {!optIsGlobal && company.code != null && (
            <span style={{ color: 'var(--dash-text-muted)', fontVariantNumeric: 'tabular-nums' }}>
              {company.code}
            </span>
          )}
          {reason ? (
            <span className="text-[11px]" style={{ color: 'var(--dash-text-muted)' }}>
              {t('dashboard.company.notAvailable')}
            </span>
          ) : !optIsGlobal && company.enabled !== undefined && (
            company.enabled ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: 'var(--dash-success)' }}>
                <Check className="w-3 h-3" />{t('dashboard.company.enabled')}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: 'var(--dash-text-muted)' }}>
                <span className="inline-block w-3 h-3 rounded-full border border-current" />
                {t('dashboard.company.notEnabled')}
              </span>
            )
          )}
        </span>
      </button>
    );
  };

  return (
    <div className={`relative inline-block ${className}`} ref={rootRef} data-testid="dashboard-company-selector">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen(v => !v)}
        className={chipClasses}
      >
        <span className="flex-shrink-0"><ContextIcon className="w-3.5 h-3.5 flex-shrink-0" /></span>
        <span className="max-w-[180px] truncate" title={chipLabel}>{chipLabel}</span>
        <ChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full mt-1 z-50 min-w-[300px] max-w-[420px] max-h-[320px] overflow-y-auto rounded-lg shadow-md py-1 border"
          style={{ background: 'var(--dash-surface-card)', borderColor: 'var(--dash-border)' }}
        >
          {showSearch && (
            <div className="px-2 pb-1">
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={t('dashboard.company.search')}
                aria-label={t('dashboard.company.search')}
                className="w-full px-2 py-1.5 text-xs rounded border bg-transparent outline-none focus:border-primary-500"
                style={{ borderColor: 'var(--dash-border)', color: 'var(--dash-text-primary)' }}
                data-testid="company-search"
              />
            </div>
          )}

          {visible.length === 0 && (
            <p className="px-3 py-3 text-xs text-center" style={{ color: 'var(--dash-text-muted)' }}>
              {t('dashboard.company.noMatches')}
            </p>
          )}

          {globals.map(renderOption)}
          {globals.length > 0 && reales.length > 0 && (
            <hr className="my-1" style={{ borderColor: 'var(--dash-border)' }} />
          )}
          {reales.map(renderOption)}
        </div>
      )}
    </div>
  );
};
